import {
  Alert,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  PageHeader,
  Skeleton,
  StatusBadge,
  Tab,
  TabList,
  TabPanel,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Tabs,
  useToast,
} from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import {
  Building2,
  ChefHat,
  Download,
  Lock,
  type LucideIcon,
  PackageCheck,
  Printer,
  RefreshCcw,
  Salad,
  UtensilsCrossed,
} from 'lucide-react';
import { type JSX, useState } from 'react';

import { useCurrentUser } from '../../auth/use-current-user';
import { OperatingUnitSelect } from '../../components/operating-unit-select';
import { GuideLink } from '../../guide/guide-link';
import { formatCalendarDate } from '../../production/format-calendar-date';
import { hasDelta } from '../../production/diff-snapshot';
import { groupByServiceType, splitByDietaryCriticality } from '../../production/group-lines';
import {
  closeProduction,
  exportConsolidatedProduction,
  exportProduction,
  getConsolidatedProduction,
  getProduction,
  registerAddendum,
  type ProductionLine,
} from '../../production/api';
import {
  isPendingOrdersError,
  isUnitRequiredError,
  mapProductionError,
} from '../../production/production-api-errors';
import { useOperatingUnitOptionsState } from '../../operating-units/use-operating-unit-options';
import { hasPermission } from '../../auth/has-permission';
import {
  resolveCatalogName,
  useDietaryTagNames,
  useServiceTypeNames,
} from '../../production/use-catalog-names';
import { AddendaBlock } from './components/addenda-block';
import { ConsolidatedProductionView } from './components/consolidated-production';
import { DietaryHighlightBlock } from './components/dietary-highlight-block';
import { ProductionLineTable } from './components/production-line-table';

export type ProductionViewTab = 'consolidado' | 'tipo-servicio' | 'sede';

const productionViewRoute = getRouteApi('/protected/production/$date');

/** Value of the "Todos los puntos" choice in the unit select (the read-only consolidated view). */
const ALL_UNITS = 'all';

const TAB_LABELS: Record<ProductionViewTab, string> = {
  consolidado: 'Consolidado',
  'tipo-servicio': 'Por tipo de servicio',
  sede: 'Por empresa y sede',
};

/** `DESAYUNO`/`ALMUERZO`/`VIANDA`/`CENA` first, any other `serviceType` code sorted after, alphabetically. */
const SERVICE_TYPE_ORDER = ['DESAYUNO', 'ALMUERZO', 'VIANDA', 'CENA'];

function sortedServiceTypes(grouped: Map<string, ProductionLine[]>): string[] {
  return [...grouped.keys()].sort((a, b) => {
    const indexA = SERVICE_TYPE_ORDER.indexOf(a);
    const indexB = SERVICE_TYPE_ORDER.indexOf(b);

    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
}

interface SummaryCardProps {
  icon: LucideIcon;
  label: string;
  value: string;
}

/** Same glanceable-card convention as `DashboardDayPage`'s own `SummaryCard`. */
function SummaryCard({ icon: Icon, label, value }: SummaryCardProps): JSX.Element {
  return (
    <Card className="flex items-center gap-3">
      <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
        <Icon aria-hidden="true" className="size-5" />
      </span>
      <div className="flex flex-col">
        <span className="text-xs font-medium text-slate-500">{label}</span>
        <span className="text-xl font-semibold tabular-nums text-slate-900">{value}</span>
      </div>
    </Card>
  );
}

/**
 * `production-sheets/spec.md` → "Pantalla de producción en back-office".
 * The date lives in the URL path; the active tab in a validated search
 * param, same convention as `contractDetailSearchSchema`.
 */
export function ProductionSheetViewPage(): JSX.Element {
  const { date } = productionViewRoute.useParams();
  const { tab } = productionViewRoute.useSearch();
  const navigate = productionViewRoute.useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currentUser = useCurrentUser();

  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [unit, setUnit] = useState('');
  const { options: unitOptions, isPending: unitsPending } = useOperatingUnitOptionsState();

  // With one visible unit there is nothing to choose: it is the unit, as before points of sale.
  const selectedUnit =
    unit !== '' ? unit : unitOptions.length === 1 ? (unitOptions[0]?.id ?? '') : '';
  const isAll = selectedUnit === ALL_UNITS;
  const operatingUnitId = selectedUnit === '' || isAll ? undefined : selectedUnit;
  const needsUnitChoice = unitOptions.length > 1 && selectedUnit === '';

  const query = useQuery({
    queryKey: ['production', date, operatingUnitId ?? ''],
    queryFn: () => getProduction(date, operatingUnitId),
    enabled: !isAll && !unitsPending && !needsUnitChoice,
  });
  const consolidatedQuery = useQuery({
    queryKey: ['production', date, 'consolidated'],
    queryFn: () => getConsolidatedProduction(date),
    enabled: isAll,
  });
  const unitRequired = needsUnitChoice || (query.isError && isUnitRequiredError(query.error));
  const serviceTypeNames = useServiceTypeNames();
  const dietaryTagNames = useDietaryTagNames();

  async function invalidate(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['production', date] });
  }

  const closeMutation = useMutation({
    mutationFn: () => closeProduction(date, operatingUnitId),
    onSuccess: async () => {
      await invalidate();
      setCloseModalOpen(false);
      toast('success', 'Planilla cerrada');
    },
  });

  const addendumMutation = useMutation({
    mutationFn: () => registerAddendum(date, operatingUnitId),
    onSuccess: async () => {
      await invalidate();
      toast('success', 'Cambios posteriores registrados');
    },
    onError: (error) => {
      toast('error', mapProductionError(error));
    },
  });

  function changeDate(nextDate: string): void {
    void navigate({ to: '/production/$date', params: { date: nextDate }, search: (prev) => prev });
  }

  function goToTab(next: ProductionViewTab): void {
    void navigate({ search: (prev) => ({ ...prev, tab: next }) });
  }

  async function handleExport(): Promise<void> {
    setExporting(true);
    try {
      await (isAll ? exportConsolidatedProduction(date) : exportProduction(date, operatingUnitId));
    } catch {
      toast('error', 'No pudimos exportar la planilla.');
    } finally {
      setExporting(false);
    }
  }

  const sheetUnitName =
    query.data?.snapshot.metadata.operatingUnit?.name ??
    unitOptions.find((option) => option.id === operatingUnitId)?.name;
  const canClose = hasPermission(currentUser.data, 'production.close');
  const canExport = hasPermission(currentUser.data, 'production.export');

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={ChefHat}
        title="Producción"
        description="Consolidado de lo que hay que cocinar para la fecha seleccionada."
        actions={
          <div className="flex items-center gap-2">
            <GuideLink moduleId="production" />
            {canExport && !isAll ? (
              <Link
                to="/production/$date/print"
                params={{ date }}
                {...(operatingUnitId === undefined ? {} : { search: { unit: operatingUnitId } })}
              >
                <Button variant="secondary" icon={Printer}>
                  Imprimir
                </Button>
              </Link>
            ) : null}
            {canExport ? (
              <Button
                variant="secondary"
                icon={Download}
                loading={exporting}
                onClick={() => void handleExport()}
              >
                Exportar CSV
              </Button>
            ) : null}
            {!isAll &&
            query.data !== undefined &&
            query.data.status === 'CLOSED' &&
            canClose &&
            hasDelta(query.data.pendingDelta) ? (
              <Button
                variant="secondary"
                icon={RefreshCcw}
                loading={addendumMutation.isPending}
                onClick={() => addendumMutation.mutate()}
              >
                Registrar cambios posteriores
              </Button>
            ) : null}
            {!isAll && query.data !== undefined && query.data.status !== 'CLOSED' && canClose ? (
              <Button icon={Lock} onClick={() => setCloseModalOpen(true)}>
                Cerrar planilla
              </Button>
            ) : null}
          </div>
        }
      />

      <Card>
        <div className="flex flex-wrap items-end gap-4">
          <Field
            label="Fecha de servicio"
            name="serviceDate"
            type="date"
            value={date}
            onChange={(event) => changeDate(event.target.value)}
          />
          <OperatingUnitSelect
            id="production-operating-unit"
            label="Punto de venta"
            required
            value={selectedUnit}
            onChange={setUnit}
            extraOption={{ value: ALL_UNITS, label: 'Todos los puntos' }}
          />
        </div>
      </Card>

      {unitRequired ? (
        <Alert tone="info">
          Elegí un punto de venta (o "Todos los puntos") para ver la planilla.
        </Alert>
      ) : null}

      {query.isLoading || consolidatedQuery.isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : null}

      {(query.isError && !unitRequired) || consolidatedQuery.isError ? (
        <Alert tone="danger" title="No pudimos cargar la planilla">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void (isAll ? consolidatedQuery.refetch() : query.refetch())}
            >
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {closeMutation.isError && isPendingOrdersError(closeMutation.error) ? (
        <Alert tone="warning" title="Todavía hay pedidos pendientes">
          <p>{mapProductionError(closeMutation.error)}</p>
          <Link to="/" className="mt-1 inline-block font-medium underline">
            Ir al Tablero del día
          </Link>
        </Alert>
      ) : closeMutation.isError ? (
        <Alert tone="danger">{mapProductionError(closeMutation.error)}</Alert>
      ) : null}

      {isAll && consolidatedQuery.data !== undefined ? (
        <ConsolidatedProductionView
          view={consolidatedQuery.data}
          serviceTypeNames={serviceTypeNames}
          dietaryTagNames={dietaryTagNames}
        />
      ) : null}

      {!isAll && query.data !== undefined ? (
        <ProductionSheetBody
          view={query.data}
          unitName={sheetUnitName}
          canClose={canClose}
          serviceTypeNames={serviceTypeNames}
          dietaryTagNames={dietaryTagNames}
        />
      ) : null}

      {closeModalOpen ? (
        <Modal
          open
          onClose={() => setCloseModalOpen(false)}
          title="Cerrar planilla"
          className="max-w-md"
        >
          <div className="flex flex-col gap-4">
            {closeMutation.isError && !isPendingOrdersError(closeMutation.error) ? (
              <Alert tone="danger">{mapProductionError(closeMutation.error)}</Alert>
            ) : null}
            <p className="text-sm text-slate-700">
              Se va a cerrar la planilla
              {sheetUnitName === undefined ? '' : ` de ${sheetUnitName}`} del{' '}
              {formatCalendarDate(date)}. Después no se puede reabrir; los cambios que lleguen se
              anotan aparte como "Cambios posteriores al cierre".
            </p>
            <div className="flex items-center gap-3">
              <Button loading={closeMutation.isPending} onClick={() => closeMutation.mutate()}>
                Confirmar cierre
              </Button>
              <Button variant="secondary" onClick={() => setCloseModalOpen(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  );

  function ProductionSheetBody({
    view,
    unitName,
    canClose: canCloseSheet,
    serviceTypeNames: serviceTypeNameMap,
    dietaryTagNames: dietaryTagNameMap,
  }: {
    view: NonNullable<typeof query.data>;
    unitName: string | undefined;
    canClose: boolean;
    serviceTypeNames: Record<string, string>;
    dietaryTagNames: Record<string, string>;
  }): JSX.Element {
    const lines = view.snapshot.lines;
    const { critical, general } = splitByDietaryCriticality(lines);
    const isEmpty = lines.length === 0 && view.addenda.length === 0 && !hasDelta(view.pendingDelta);

    const distinctSites = new Set(
      lines.flatMap((line) => line.byCustomerSite.map((b) => `${b.customerId}:${b.siteId}`)),
    );

    return (
      <div className="flex flex-col gap-6">
        {unitName === undefined ? null : (
          <h2 data-testid="production-unit-name" className="text-lg font-semibold text-slate-900">
            {unitName}
          </h2>
        )}
        <div className="flex items-center gap-3">
          <StatusBadge domain="production" status={view.status} />
          {view.status === 'PROVISIONAL' ? (
            <span className="text-sm text-slate-500">
              Provisoria — puede cambiar hasta el cierre
            </span>
          ) : null}
        </div>

        {isEmpty ? (
          <EmptyState icon={ChefHat} title="No hay pedidos para esta fecha" />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <SummaryCard
                icon={PackageCheck}
                label="Unidades totales"
                value={String(view.snapshot.metadata.totalQuantity)}
              />
              <SummaryCard icon={UtensilsCrossed} label="Menús" value={String(lines.length)} />
              <SummaryCard
                icon={Building2}
                label="Empresas y sedes"
                value={String(distinctSites.size)}
              />
              <SummaryCard icon={Salad} label="Menús con dieta especial" value={String(critical.length)} />
            </div>

            {hasDelta(view.pendingDelta) ? (
              <Alert tone="warning" title="Hay cambios sin registrar">
                Los pedidos confirmados cambiaron desde el cierre de esta planilla.
                {canCloseSheet ? ' Usá "Registrar cambios posteriores" para dejarlo asentado.' : ''}
              </Alert>
            ) : null}

            <Tabs value={tab} onValueChange={(value) => goToTab(value as ProductionViewTab)}>
              <TabList aria-label="Vistas del consolidado">
                {(Object.keys(TAB_LABELS) as ProductionViewTab[]).map((key) => (
                  <Tab key={key} value={key}>
                    {TAB_LABELS[key]}
                  </Tab>
                ))}
              </TabList>

              <TabPanel value="consolidado">
                <div className="flex flex-col gap-4">
                  <DietaryHighlightBlock
                    lines={critical}
                    serviceTypeNames={serviceTypeNameMap}
                    dietaryTagNames={dietaryTagNameMap}
                  />
                  {general.length > 0 ? (
                    <div data-testid="production-general-table">
                      <ProductionLineTable
                        lines={general}
                        serviceTypeNames={serviceTypeNameMap}
                        dietaryTagNames={dietaryTagNameMap}
                      />
                    </div>
                  ) : null}
                </div>
              </TabPanel>

              <TabPanel value="tipo-servicio">
                <div className="flex flex-col gap-6">
                  {sortedServiceTypes(groupByServiceType(lines)).map((serviceType) => (
                    <div key={serviceType} className="flex flex-col gap-2">
                      <h3 className="text-sm font-semibold text-slate-700">
                        {resolveCatalogName(serviceTypeNameMap, serviceType)}
                      </h3>
                      <ProductionLineTable
                        lines={groupByServiceType(lines).get(serviceType) ?? []}
                        serviceTypeNames={serviceTypeNameMap}
                        dietaryTagNames={dietaryTagNameMap}
                      />
                    </div>
                  ))}
                </div>
              </TabPanel>

              <TabPanel value="sede">
                <SiteBreakdownTable lines={lines} />
              </TabPanel>
            </Tabs>

            <AddendaBlock addenda={view.addenda} />
          </>
        )}
      </div>
    );
  }
}

function SiteBreakdownTable({ lines }: { lines: ProductionLine[] }): JSX.Element {
  const rows = lines
    .flatMap((line) =>
      line.byCustomerSite.map((breakdown) => ({
        menuName: line.menuName,
        customerName: breakdown.customerName,
        siteName: breakdown.siteName,
        quantity: breakdown.quantity,
        key: `${line.menuId}:${breakdown.customerId}:${breakdown.siteId}`,
      })),
    )
    .sort(
      (a, b) =>
        a.customerName.localeCompare(b.customerName) ||
        a.siteName.localeCompare(b.siteName) ||
        a.menuName.localeCompare(b.menuName),
    );

  return (
    <Table>
      <caption className="sr-only">Desglose por empresa y sede</caption>
      <thead>
        <tr>
          <TableHeader>Empresa</TableHeader>
          <TableHeader>Sede</TableHeader>
          <TableHeader>Menú</TableHeader>
          <TableHeader>Cantidad</TableHeader>
        </tr>
      </thead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.key}>
            <TableCell>{row.customerName}</TableCell>
            <TableCell>{row.siteName}</TableCell>
            <TableCell>{row.menuName}</TableCell>
            <TableCell className="font-medium tabular-nums">{row.quantity}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
